/**
 * Voice input + TTS. Web Speech API for ASR (Hindi-IN + English-IN).
 * TTS prefers Sarvam Bulbul V3 if SARVAM_KEY injected; falls back to SpeechSynthesis.
 */

type SpeechRecognitionEventLike = { results: ArrayLike<ArrayLike<{ transcript: string; confidence: number }>>; resultIndex: number };
type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: Event) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

interface SpeechWin {
  SpeechRecognition?: new () => SpeechRecognitionLike;
  webkitSpeechRecognition?: new () => SpeechRecognitionLike;
}

export function speechSupported(): boolean {
  const w = window as unknown as SpeechWin;
  return Boolean(w.SpeechRecognition ?? w.webkitSpeechRecognition);
}

export interface ListenOpts {
  lang?: 'hi-IN' | 'en-IN' | 'en-US';
  onPartial?: (text: string) => void;
}

export async function listenOnce(opts: ListenOpts = {}): Promise<string> {
  const w = window as unknown as SpeechWin;
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  if (!Ctor) throw new Error('speech-recognition-unavailable');
  const rec = new Ctor();
  rec.lang = opts.lang ?? 'hi-IN';
  rec.continuous = false;
  rec.interimResults = true;
  rec.maxAlternatives = 1;
  return new Promise<string>((resolve, reject) => {
    let final = '';
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (!res) continue;
        const alt = res[0];
        if (!alt) continue;
        if ((res as unknown as { isFinal: boolean }).isFinal) {
          final += alt.transcript;
        } else {
          opts.onPartial?.(alt.transcript);
        }
      }
    };
    rec.onerror = (e) => reject(new Error(`speech-error:${(e as unknown as { error?: string }).error ?? 'unknown'}`));
    rec.onend = () => resolve(final.trim());
    rec.start();
  });
}

export interface TtsOpts {
  lang?: 'hi-IN' | 'en-IN';
  rate?: number;
  pitch?: number;
  voice?: string;
}

export async function speak(text: string, opts: TtsOpts = {}): Promise<void> {
  const key = (window as unknown as { __SARVAM_KEY__?: string }).__SARVAM_KEY__;
  if (key) {
    try {
      await speakSarvam(text, key, opts);
      return;
    } catch {
      // fall through to local
    }
  }
  await speakLocal(text, opts);
}

async function speakLocal(text: string, opts: TtsOpts): Promise<void> {
  return new Promise((resolve) => {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = opts.lang ?? 'hi-IN';
    u.rate = opts.rate ?? 1;
    u.pitch = opts.pitch ?? 1;
    if (opts.voice) {
      const match = window.speechSynthesis.getVoices().find((v) => v.name === opts.voice);
      if (match) u.voice = match;
    }
    u.onend = () => resolve();
    u.onerror = () => resolve();
    window.speechSynthesis.speak(u);
  });
}

async function speakSarvam(text: string, apiKey: string, opts: TtsOpts): Promise<void> {
  const r = await fetch('https://api.sarvam.ai/text-to-speech', {
    method: 'POST',
    headers: { 'api-subscription-key': apiKey, 'content-type': 'application/json' },
    body: JSON.stringify({
      inputs: [text],
      target_language_code: opts.lang === 'en-IN' ? 'en-IN' : 'hi-IN',
      speaker: opts.voice ?? 'meera',
      pitch: opts.pitch ?? 0,
      pace: opts.rate ?? 1,
      loudness: 1.5,
      enable_preprocessing: true,
      model: 'bulbul:v3',
    }),
  });
  if (!r.ok) throw new Error(`sarvam-${r.status}`);
  const body = await r.json() as { audios: string[] };
  const b64 = body.audios[0];
  if (!b64) throw new Error('sarvam-empty');
  const audio = new Audio(`data:audio/wav;base64,${b64}`);
  await audio.play();
  await new Promise<void>((resolve) => {
    audio.onended = () => resolve();
    audio.onerror = () => resolve();
  });
}
